namespace PortalLibs.Client


open PortalLibs.Shared
open PortalLibs.Shared.Api
open Thoth.Json
open Elmish
open Browser.WebStorage
open Browser
open Elmish.Bridge
open Fable.Remoting.Client
open FSharpPlus

type User =
  | LoggingIn
  | LoggedIn of UserData
  | LoggedOut
  | AuthException of exn

module User =
  let private tryLoggedIn =
    function
    | LoggedIn userData -> Some userData
    | _ -> None
  
  let canSwitchRoles =
    tryLoggedIn
    >> map UserData.canSwitchRoles
    >> Option.defaultValue false
  
  let tryUserEmail =
    tryLoggedIn
    >> map (fun u -> u.Email)
    >> Option.defaultValue "Unknown"
    
  let tryEmailsMatch email =
    tryLoggedIn
    >> map (UserData.emailsMatch email)
    
module Auth =
  type Model = { User: User }

  type ExternalMsg =
    | NoOp
    | LoggedIn

  type Msg =
    | NoOp
    | StorageFailure of exn
    | LogIn
    | LogInResult of Result<UserData, exn>
    | LoggedIn
    | LoggedOut
    | LogOut
    
  let loadUser () : UserData option =
    match Decode.Auto.fromString<UserData> (localStorage.getItem "user") with
    | Ok user -> Some user
    | Result.Error _ -> None


  let saveUserCmd user =
    Cmd.OfFunc.either
      localStorage.setItem
      ("user", (Encode.Auto.toString<UserData> (2, user)))
      (fun _ -> LoggedIn)
      StorageFailure

  let deleteUserCmd =
    Cmd.OfFunc.either localStorage.removeItem "user" (fun _ -> LoggedOut) StorageFailure

  let api: IRemoteProtocol =
    Remoting.createApi ()
    |> Remoting.withRouteBuilder Route.clientBuilder
    |> Remoting.buildProxy<IRemoteProtocol>


  let init () =
    let user = loadUser ()

    { User = LoggingIn },
    (match user with
     | Some u -> Cmd.ofMsg (LogInResult(Ok u))
     | None -> Cmd.ofMsg LogIn)

  let update msg model =
    match msg with
    | LogIn ->
      model,
      Cmd.OfAsyncFromTask.either
        api.getUserData
        ()
        (Ok >> LogInResult)
        (Result.Error >> LogInResult),
      ExternalMsg.NoOp
    | LogInResult res ->
      match res with
      | Ok userData ->
        { model with
            User = User.LoggedIn userData },
        saveUserCmd userData,
        ExternalMsg.NoOp
      | Result.Error error ->
        { model with
            User =
              if error.Message.Contains("Http error (401)") then
                model.User
              else
                User.AuthException error },
        (if error.Message.Contains("Http error (401)") then
           Cmd.OfFunc.perform (fun () -> window.location.href <- "/login") () (fun () -> NoOp)
         else
           Cmd.none),
        ExternalMsg.NoOp
    | LogOut ->
      { model with User = User.LoggedOut },
      Cmd.batch [
        deleteUserCmd
        Cmd.OfFunc.perform (fun () -> window.location.href <- "/logout") () (fun () -> NoOp)
      ],
      ExternalMsg.NoOp
    | LoggedIn -> model, Cmd.none, ExternalMsg.LoggedIn
    | StorageFailure e -> model, $"Auth Exception: %A{e}" |> Notifications.error, ExternalMsg.NoOp
    | NoOp
    | LoggedOut -> model, Cmd.none, ExternalMsg.NoOp
